import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { profileSelectors, userSelectors } from '@/redux/selectors';
import { userActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { navigationPages, PageDescription } from '@/config';
import { simulateMouseClick } from '@/helpers';
import { chain } from 'lodash';

import { SidebarNavLink } from './components/SidebarNavLink';
import { SidebarNavButton } from './components/SidebarNavButton';
import { SidebarNavSubmenuLink } from './components/SidebarNavSubmenuLink';

import styles from './SidebarNav.module.sass';

const isComplianceDashboardAvailable =
  import.meta.env.VITE_REACT_APP_COMPLIANCE_AVAILABLE?.toLowerCase() === 'true';

type SidebarPages = Record<
  string,
  {
    order: number;
    isVisible: boolean;
  }
>;

interface SidebarNavProps {
  isB2B?: boolean;
}

const SidebarNav = ({ isB2B }: SidebarNavProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const isCheckOnly = useAppSelector(userSelectors.checkIfCheckOnly);
  const isTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);
  const isTraceIncluded = useAppSelector(userSelectors.checkIfAiTraceIncluded);
  const isProPlus = useAppSelector(profileSelectors.isProPlus);

  // Signing out ends with a full page redirect, so the flag never resets.
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signoutHandler = () => {
    setIsSigningOut(true);
    dispatch(userActions.signout());
  };

  const goToTracer = () => {
    if (userData?.proMode) {
      const proHref = (import.meta.env
        .VITE_REACT_APP_PRO_AMLBOT_URL as string)!;
      window.open(proHref, '_blank');
    } else {
      const ctaButton = document.getElementById('hs-cta-trigger-button_pro');
      if (ctaButton) {
        simulateMouseClick(ctaButton as HTMLButtonElement);
      }
    }
  };

  const sidebarPages = useMemo(() => {
    if (isTraceOnly) {
      return {
        TRACE_INVESTIGATIONS: { order: 1, isVisible: true },
        TARIFFS: { order: 2, isVisible: true },
      } as SidebarPages;
    }

    const sidebarPages = {
      DASHBOARD: { order: isProPlus ? 2 : 1, isVisible: true },
      COMPLIANCE_DASHBOARD: {
        order: isProPlus ? 1 : 2,
        isVisible: isComplianceDashboardAvailable && !isCheckOnly,
      },
      TARIFFS: { order: 5, isVisible: true },
    } as SidebarPages;

    if (isTraceIncluded) {
      sidebarPages.TRACE_INVESTIGATIONS = { order: 0, isVisible: true };
    }

    return sidebarPages;
  }, [isTraceOnly, isTraceIncluded, isCheckOnly, isProPlus]);

  const listPages = useMemo(() => {
    return chain(sidebarPages)
      .toPairs()
      .filter(([_, value]) => value.isVisible)
      .sortBy(([_, value]) => value.order)
      .map(([key]) => ({ key, ...navigationPages[key] }))
      .value();
  }, [sidebarPages]);

  return (
    <nav className={styles.nav}>
      <div className={styles.scope}>
        {listPages.map(
          ({ iconName, i18NameKey, path, subMenu }: PageDescription) => {
            if (subMenu) {
              return (
                <SidebarNavSubmenuLink
                  key={path}
                  iconName={iconName}
                  text={t(i18NameKey)}
                  subMenu={Object.values(subMenu)}
                  to={path}
                />
              );
            }

            return (
              <SidebarNavLink
                key={path}
                iconName={iconName}
                text={t(i18NameKey)}
                to={path}
              />
            );
          },
        )}
      </div>

      {!isTraceOnly && (
        <button type="button" className={styles.pro} onClick={goToTracer}>
          <span className={styles.text}>
            {t('translation.need_deep_investigation')}
          </span>
          <span className={styles.link}>{t('translation.try_amlbot_pro')}</span>
        </button>
      )}

      <div className={styles.bottom}>
        <SidebarNavButton
          iconName="logout"
          text={t('translation.sign_out')}
          onClick={signoutHandler}
          className={styles.logout}
          isLoading={isSigningOut}
        />
      </div>
    </nav>
  );
};

export default SidebarNav;
